import StatsWidget from '@rio-cloud/rio-uikit/StatsWidget';
import StatsWidgets from '@rio-cloud/rio-uikit/StatsWidgets';
import type { KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { PDC_CONDITION_OVERDUE, PDC_SEVERITY_HIGH, PDC_SEVERITY_LOW, PDC_SEVERITY_MEDIUM } from '@/constants';
import { getInspectionsSummary } from '@/selectors';

export type InspectionsSummary = {
    critical: number;
    failed: number;
    overdue: number;
    withoutIssues: number;
};

type OverviewTileProps = {
    active: boolean;
    footer?: string;
    icon: string;
    label: string;
    onClick: () => void;
    value?: number;
    valueClassName: string;
};

type PDCOverviewTilesProps = {
    selectedConditions: string[];
    onConditionsChange: (conditions: string[]) => void;
};

export const OVERVIEW_TILE_CONDITIONS = {
    critical: [PDC_SEVERITY_HIGH],
    failed: [PDC_SEVERITY_MEDIUM],
    overdue: [PDC_CONDITION_OVERDUE],
    withoutIssues: [PDC_SEVERITY_LOW],
};

export const isSameConditionSelection = (selectedConditions: string[], conditions: string[]) =>
    selectedConditions.length === conditions.length &&
    conditions.every(condition => selectedConditions.includes(condition));

const OverviewTile = ({ active, footer, icon, label, onClick, value, valueClassName }: OverviewTileProps) => {
    const displayValue = value ?? '-';
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
        }
    };

    return (
        <StatsWidget className='shadow-default display-flex flex-column' hasFilter={active} onFilterReset={onClick}>
            {/* biome-ignore lint/a11y/useSemanticElements: The entire KPI card is the click target. */}
            <div
                aria-label={`${label}: ${displayValue}`}
                aria-pressed={active}
                className='display-flex flex-column flex-1-1 cursor-pointer'
                onClick={onClick}
                onKeyDown={handleKeyDown}
                role='button'
                tabIndex={0}
            >
                <div className='flex-1-1 display-flex gap-15 padding-20'>
                    <div className='height-50 aspect-ratio-1 bg-lightest rounded display-grid place-items-center text-size-14'>
                        <span className={`uikit-icon ${icon} text-size-20 ${valueClassName}`} aria-hidden='true' />
                    </div>
                    <div>
                        <div className='text-color-dark'>{label}</div>
                        <div className='text-size-h3 text-color-darkest line-height-125rel'>{displayValue}</div>
                    </div>
                </div>
                {footer && (
                    <div className='text-size-12 padding-x-20 padding-y-10 bg-lightest rounded-bottom display-flex gap-5'>
                        <span className='text-color-dark'>{footer}</span>
                    </div>
                )}
            </div>
        </StatsWidget>
    );
};

const PDCOverviewTiles = ({ selectedConditions, onConditionsChange }: PDCOverviewTilesProps) => {
    const { formatMessage } = useIntl();
    const summary: InspectionsSummary | null | undefined = useSelector(getInspectionsSummary);
    const isActive = (conditions: string[]) => isSameConditionSelection(selectedConditions, conditions);
    const toggle = (conditions: string[]) => () => onConditionsChange(isActive(conditions) ? [] : conditions);

    return (
        <StatsWidgets className='display-grid grid-auto-fit-250 margin-bottom-20'>
            <OverviewTile
                active={isActive(OVERVIEW_TILE_CONDITIONS.critical)}
                icon='icon-error-sign'
                label={formatMessage({ id: 'intl-msg:pdc.overview.critical.label' })}
                value={summary?.critical}
                valueClassName='text-color-danger'
                footer={formatMessage({ id: 'intl-msg:pdc.overview.critical.footer' })}
                onClick={toggle(OVERVIEW_TILE_CONDITIONS.critical)}
            />
            <OverviewTile
                active={isActive(OVERVIEW_TILE_CONDITIONS.failed)}
                icon='icon-warning-sign'
                label={formatMessage({ id: 'intl-msg:pdc.overview.failed.label' })}
                value={summary?.failed}
                valueClassName='text-color-warning'
                footer={formatMessage({ id: 'intl-msg:pdc.overview.failed.footer' })}
                onClick={toggle(OVERVIEW_TILE_CONDITIONS.failed)}
            />
            <OverviewTile
                active={isActive(OVERVIEW_TILE_CONDITIONS.overdue)}
                icon='icon-time'
                label={formatMessage({ id: 'intl-msg:pdc.overview.overdue.label' })}
                value={summary?.overdue}
                valueClassName='text-color-info'
                footer={formatMessage({ id: 'intl-msg:pdc.overview.overdue.footer' })}
                onClick={toggle(OVERVIEW_TILE_CONDITIONS.overdue)}
            />
            <OverviewTile
                active={isActive(OVERVIEW_TILE_CONDITIONS.withoutIssues)}
                icon='icon-check-badge'
                label={formatMessage({ id: 'intl-msg:pdc.overview.withoutIssues.label' })}
                value={summary?.withoutIssues}
                valueClassName='text-color-success'
                footer={formatMessage({ id: 'intl-msg:pdc.overview.withoutIssues.footer' })}
                onClick={toggle(OVERVIEW_TILE_CONDITIONS.withoutIssues)}
            />
        </StatsWidgets>
    );
};

export default PDCOverviewTiles;
